import { BillingCycle, PlanApiData, PlanId } from 'src/app/pages/new-billing/types/api.types';
import { LegacyPricingPlans, PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';

export type PlanProductId = PlanApiData['productId'];

export const planTierRank: Record<PlanProductId, number> = {
  basic: 1,
  standard: 2,
  premium: 3,
};

export function getPlanTierFromPlanId(planId: string | undefined): number {
  if (!planId) return 0;

  const productId = planId.split('_')[0] as PlanProductId;
  return planTierRank[productId] ?? 0;
}

/**
 * Parses plan id suffix for billing rhythm (paddle-style ids such as basic_monthly_01).
 */
export function extractBillingCycleFromPlanId(planId: string | undefined): BillingCycle | null {
  if (!planId?.trim()) {
    return null;
  }
  const id = planId.toLowerCase();
  if (id.includes('semi_yearly')) {
    return 'semi_yearly';
  }
  if (id.includes('quarterly')) {
    return 'quarterly';
  }
  if (id.includes('monthly')) {
    return 'monthly';
  }
  if (id.includes('yearly')) {
    return 'yearly';
  }
  return null;
}

export const billingCycleRank: Record<BillingCycle, number> = {
  monthly: 1,
  quarterly: 2,
  semi_yearly: 3,
  yearly: 4,
};

export function isPlanUpgrade(
  previousPlanId: string | undefined,
  currentPlanId: string | undefined,
): boolean {
  if (!previousPlanId || !currentPlanId || previousPlanId === currentPlanId) {
    return false;
  }

  const prevTier = getPlanTierFromPlanId(previousPlanId);
  const currTier = getPlanTierFromPlanId(currentPlanId);
  if (prevTier <= 0 || currTier <= 0) {
    return false;
  }
  if (currTier > prevTier) {
    return true;
  }
  if (currTier < prevTier) {
    return false;
  }

  const prevCycle = extractBillingCycleFromPlanId(previousPlanId);
  const currCycle = extractBillingCycleFromPlanId(currentPlanId);
  if (!prevCycle || !currCycle) {
    return false;
  }
  return billingCycleRank[currCycle] > billingCycleRank[prevCycle];
}

export const planIdToPricingPlan: Record<PlanId, LegacyPricingPlans> = {
  standard_yearly: 'standard_plan_annual',
  standard_semi_yearly: 'standard_plan_semi_annual',
  standard_quarterly: 'standard_plan_quarter',
  standard_monthly: 'standard_plan',
  basic_yearly: 'basic_plan_annual',
  basic_semi_yearly: 'basic_plan_semi_annual',
  basic_quarterly: 'basic_plan_quarter',
  basic_monthly: 'basic_plan',
  premium_yearly: 'premium_plan_annual',
  premium_semi_yearly: 'premium_plan_semi_annual',
  premium_quarterly: 'premium_plan_quarter',
  premium_monthly: 'premium_plan',
  standard_yearly_01: 'standard_plan_annual',
  standard_semi_yearly_01: 'standard_plan_semi_annual',
  standard_quarterly_01: 'standard_plan_quarter',
  standard_monthly_01: 'standard_plan',
  basic_yearly_01: 'basic_plan_annual',
  basic_semi_yearly_01: 'basic_plan_semi_annual',
  basic_quarterly_01: 'basic_plan_quarter',
  basic_monthly_01: 'basic_plan',
};

export const pricingPlanToPlanId: Record<LegacyPricingPlans, PlanId> = {
  basic_plan_annual: 'basic_yearly_01',
  basic_plan_semi_annual: 'basic_semi_yearly_01',
  basic_plan_quarter: 'basic_quarterly_01',
  basic_plan: 'basic_monthly_01',
  standard_plan_annual: 'standard_yearly_01',
  standard_plan_semi_annual: 'standard_semi_yearly_01',
  standard_plan_quarter: 'standard_quarterly_01',
  standard_plan: 'standard_monthly_01',
  premium_plan_annual: 'premium_yearly',
  premium_plan_semi_annual: 'premium_semi_yearly',
  premium_plan_quarter: 'premium_quarterly',
  premium_plan: 'premium_monthly',
};

export const tierUnderPlan: Record<PlanId, PlanId> = {
  basic_yearly: 'basic_yearly',
  basic_semi_yearly: 'basic_semi_yearly',
  basic_quarterly: 'basic_quarterly',
  basic_monthly: 'basic_monthly',
  basic_yearly_01: 'basic_yearly_01',
  basic_semi_yearly_01: 'basic_semi_yearly_01',
  basic_quarterly_01: 'basic_quarterly_01',
  basic_monthly_01: 'basic_monthly_01',
  standard_yearly: 'basic_yearly_01',
  standard_semi_yearly: 'basic_semi_yearly_01',
  standard_quarterly: 'basic_quarterly_01',
  standard_monthly: 'basic_monthly_01',
  premium_yearly: 'standard_yearly_01',
  premium_semi_yearly: 'standard_semi_yearly_01',
  premium_quarterly: 'standard_quarterly_01',
  premium_monthly: 'standard_monthly_01',
  standard_yearly_01: 'basic_yearly_01',
  standard_semi_yearly_01: 'basic_semi_yearly_01',
  standard_quarterly_01: 'basic_quarterly_01',
  standard_monthly_01: 'basic_monthly_01',
};

export const tierOverPlan: Record<PlanId, PlanId> = {
  basic_yearly: 'standard_yearly_01',
  basic_semi_yearly: 'standard_semi_yearly_01',
  basic_quarterly: 'standard_quarterly_01',
  basic_monthly: 'standard_monthly_01',
  basic_yearly_01: 'standard_yearly_01',
  basic_semi_yearly_01: 'standard_semi_yearly_01',
  basic_quarterly_01: 'standard_quarterly_01',
  basic_monthly_01: 'standard_monthly_01',
  standard_yearly: 'premium_yearly',
  standard_semi_yearly: 'premium_semi_yearly',
  standard_quarterly: 'premium_quarterly',
  standard_monthly: 'premium_monthly',
  premium_yearly: 'premium_yearly',
  premium_semi_yearly: 'premium_semi_yearly',
  premium_quarterly: 'premium_quarterly',
  premium_monthly: 'premium_monthly',
  standard_yearly_01: 'premium_yearly',
  standard_semi_yearly_01: 'premium_semi_yearly',
  standard_quarterly_01: 'premium_quarterly',
  standard_monthly_01: 'premium_monthly',
};

export const planIdSwitch: Record<PlanId<'yearly' | 'monthly'>, PlanId<'yearly' | 'monthly'>> = {
  basic_yearly: 'basic_monthly',
  basic_monthly: 'basic_yearly',
  basic_monthly_01: 'basic_yearly_01',
  basic_yearly_01: 'basic_monthly_01',
  standard_monthly: 'standard_yearly',
  standard_yearly: 'standard_monthly',
  premium_monthly: 'premium_yearly',
  premium_yearly: 'premium_monthly',
  standard_monthly_01: 'standard_yearly_01',
  standard_yearly_01: 'standard_monthly_01',
};

export const pricingPlanSwitch: Record<LegacyPricingPlans<'annual' | 'monthly'>, LegacyPricingPlans<'annual' | 'monthly'>> = {
  basic_plan: 'basic_plan_annual',
  basic_plan_annual: 'basic_plan',
  standard_plan: 'standard_plan_annual',
  standard_plan_annual: 'standard_plan',
  premium_plan: 'premium_plan_annual',
  premium_plan_annual: 'premium_plan',
};

export const pricePeriodByBillingPeriod: Record<PlanApiData['billingPeriod'], PricePeriodicity> = {
  monthly: 'month',
  yearly: 'year',
  semi_yearly: 'semi_year',
  quarterly: 'quarter',
};

export const monthlyMultiplierByRecurrence: Record<PricePeriodicity, number> = {
  month: 1,
  year: 12,
  semi_year: 6,
  quarter: 3,
};
